import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import {
  Star,
  X,
  RefreshCw,
  Check,
  AlertTriangle,
  Info,
  ExternalLink,
  Lock,
  ArrowRight,
  Plug,
} from "lucide-react";
import { useCompanyId } from "@/lib/CompanyContext";
import {
  integrationsApi,
  type CompanyIntegration,
  type IntegrationCredentialField,
} from "@/api/integrations";

type FilterTab = "todas" | "conectadas" | "nao_conectadas";

const CATEGORY_ORDER = ["Anúncios", "Analytics", "Vendas", "Dados", "Automação"];

// Logo do integration ou fallback pro emoji. onError detecta favicon que não carregou.
function IntegrationLogo({
  integration,
  size = 36,
}: {
  integration: CompanyIntegration;
  size?: number;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = integration.logoUrl && !imgFailed;
  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 8,
        background: "var(--wf-paper)",
        border: "1px solid var(--wf-ink-5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {showImg ? (
        <img
          src={integration.logoUrl!}
          alt={integration.name}
          onError={() => setImgFailed(true)}
          style={{
            width: "70%",
            height: "70%",
            objectFit: "contain",
          }}
        />
      ) : (
        <span style={{ fontSize: size * 0.5 }}>{integration.logoEmoji ?? "🔌"}</span>
      )}
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: IntegrationCredentialField;
  value: string;
  onChange: (v: string) => void;
}) {
  const inputType =
    field.type === "password" ? "password" : field.type === "url" ? "url" : "text";

  const inputStyle: React.CSSProperties = {
    background: "var(--wf-paper)",
    border: "1px solid var(--wf-ink-5)",
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 13,
    fontFamily: "var(--wf-font-body)",
    color: "var(--wf-ink)",
    width: "100%",
    outline: "none",
  };

  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--wf-ink-2)",
        }}
      >
        {field.label}
        {field.required && <span style={{ color: "var(--nd-orange-pun)" }}> *</span>}
      </span>
      <input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        style={inputStyle}
        autoComplete="off"
      />
      {field.helpText && (
        <span style={{ fontSize: 11, color: "var(--wf-ink-3)", lineHeight: 1.4 }}>
          {field.helpText}
        </span>
      )}
    </label>
  );
}

// Instruções por integração — passos numerados em linguagem amigável.
type IntegrationInstructions = {
  hero: string;
  steps: Array<{ title: string; description?: string }>;
  docsUrl?: string;
  futureTutorial?: string;
};

const INSTRUCTIONS: Record<string, IntegrationInstructions> = {
  meta_ads: {
    hero:
      "Conectando Meta Ads, sua equipe lê suas campanhas reais antes de sugerir copy, criativo ou otimização.",
    steps: [
      {
        title: "Acesse o Meta Business Suite",
        description: "Vá em business.facebook.com → menu superior → Configurações de Negócios.",
      },
      {
        title: "Vá em Usuários do Sistema",
        description:
          'Em "Usuários" → "Usuários do Sistema". Clique "Adicionar" e crie um usuário com função Admin.',
      },
      {
        title: "Gere o Access Token",
        description:
          'No usuário criado, clique "Gerar novo token". Selecione seu app, marque permissões "ads_read" e "ads_management". Copia o código longo gerado.',
      },
      {
        title: "Pegue o Ad Account ID",
        description:
          'Em "Contas" → "Contas de anúncios", copia o ID da conta. Vai começar com "act_" seguido de números.',
      },
    ],
    docsUrl: "https://developers.facebook.com/docs/marketing-api/system-users",
    futureTutorial: "/aulas/conectar-meta-ads",
  },
  google_ads: {
    hero:
      "Conectando Google Ads, sua equipe analisa campanhas de busca, display e Performance Max com dados reais (custo, CTR, CPC, conversões).",
    steps: [
      {
        title: "Solicite Developer Token no Google Ads",
        description:
          "Em ads.google.com → Tools (chave inglesa) → API Center → solicite acesso. Test account libera na hora; produção leva até 48h.",
      },
      {
        title: "Pegue o Customer ID",
        description:
          "Topo direito da tela do Google Ads, no formato 123-456-7890. Se você acessa via MCC (manager account), também precisa do ID do MCC pra preencher abaixo.",
      },
      {
        title: "Crie um OAuth 2.0 Client no Google Cloud",
        description:
          "Em console.cloud.google.com → APIs & Services → Credentials → Create credentials → OAuth client ID → 'Desktop app'. Copia Client ID e Client Secret.",
      },
      {
        title: "Gere o Refresh Token no OAuth Playground",
        description:
          "Em developers.google.com/oauthplayground → engrenagem → marca 'Use your own OAuth credentials' e cola Client ID + Secret. Em 'Step 1', cola escopo: https://www.googleapis.com/auth/adwords. Autoriza, troca código pelos tokens, e copia o Refresh Token.",
      },
    ],
    docsUrl: "https://developers.google.com/google-ads/api/docs/oauth/overview",
  },
  hotmart: {
    hero:
      "Conectando Hotmart, sua equipe analisa vendas, churn, LTV e padrões de compra dos seus alunos.",
    steps: [
      { title: "Acesse o painel da Hotmart", description: "app.hotmart.com → Ferramentas → Credenciais Hotmart" },
      { title: "Crie um app", description: 'Clique "Criar credencial" e dê um nome (ex: "Mohdus").' },
      {
        title: "Copie Client ID e Secret",
        description: "A Hotmart vai gerar Client ID e Client Secret. Cole os dois nos campos abaixo.",
      },
    ],
    docsUrl: "https://developers.hotmart.com/docs/pt-BR/start/credentials/",
  },
  ms_clarity: {
    hero:
      "Conectando Microsoft Clarity, sua equipe analisa heatmaps e gravações pra identificar onde os usuários travam.",
    steps: [
      { title: "Vá em clarity.microsoft.com", description: "Faça login na sua conta Microsoft." },
      {
        title: "Selecione seu projeto",
        description: 'Topo direito → "Configurações" → "Geração de dados" → "Projeto".',
      },
      {
        title: "Gere a API token",
        description: 'Em "Configurações" → "API Data Export" → "Generate new API token". Copia o código.',
      },
    ],
    docsUrl: "https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api",
  },
  manychat: {
    hero:
      "Conectando ManyChat, sua equipe automatiza DMs, stories e fluxos no Instagram + Messenger.",
    steps: [
      { title: "Vá em manychat.com", description: "Faça login na sua conta." },
      {
        title: "Settings → API",
        description:
          'Menu lateral → "Settings" → "API". Clique "Generate Token" pra gerar uma API key.',
      },
      { title: "Cole a API key abaixo", description: "Copie o token gerado e cole no campo." },
    ],
    docsUrl: "https://api.manychat.com/swagger",
  },
};

const DEFAULT_INSTRUCTIONS: IntegrationInstructions = {
  hero: "Preencha os campos abaixo pra conectar essa ferramenta.",
  steps: [
    {
      title: "Pegue suas credenciais",
      description:
        "Acesse a plataforma da ferramenta e gere as credenciais necessárias (geralmente em Configurações ou API).",
    },
    { title: "Cole nos campos abaixo", description: "Salvamos com criptografia local. Só sua equipe acessa." },
  ],
};

// ─────────────────────────────────────────────────────────────
// Drawer (slide-in da direita) — substitui o modal full-screen
// ─────────────────────────────────────────────────────────────

function ConnectDrawer({
  integration,
  companyId,
  onClose,
}: {
  integration: CompanyIntegration;
  companyId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<
    | { kind: "ok"; accountName: string }
    | { kind: "fail"; error: string }
    | null
  >(null);
  const isConnected = integration.connection.connected;
  const instructions = INSTRUCTIONS[integration.key] ?? DEFAULT_INSTRUCTIONS;

  const testConnection = useMutation({
    mutationFn: () => integrationsApi.test(companyId, integration.key),
    onSuccess: (data) => {
      if ("ok" in data && data.ok) {
        setTestResult({ kind: "ok", accountName: data.accountName });
      } else if ("error" in data) {
        setTestResult({ kind: "fail", error: (data as { error: string }).error });
      }
    },
    onError: (err) => {
      setTestResult({ kind: "fail", error: (err as Error).message });
    },
  });

  useEffect(() => {
    if (isConnected && integration.runtimeReady && testResult === null && !testConnection.isPending) {
      testConnection.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = useMutation({
    mutationFn: () => integrationsApi.connect(companyId, integration.key, values),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "integrations"] });
      if (integration.runtimeReady) {
        setTestResult(null);
        testConnection.mutate();
      } else {
        onClose();
      }
    },
  });

  const disconnect = useMutation({
    mutationFn: () => integrationsApi.disconnect(companyId, integration.key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "integrations"] });
      onClose();
    },
  });

  const requiredMissing = integration.credentialFields.some(
    (f) => f.required && !values[f.key]?.trim(),
  );
  const hasAnyValue = Object.values(values).some((v) => v.trim().length > 0);

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(5,8,16,0.45)",
          backdropFilter: "blur(4px)",
          zIndex: 50,
        }}
      />
      {/* Drawer slide-in */}
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "min(560px, 100%)",
          background: "var(--wf-bg)",
          borderLeft: "1px solid var(--wf-ink-5)",
          boxShadow: "-12px 0 40px rgba(5,8,16,0.20)",
          zIndex: 51,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header — sticky, com logo grande, nome, botão fechar */}
        <header
          style={{
            padding: "18px 22px 16px",
            borderBottom: "1px solid var(--wf-ink-5)",
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexShrink: 0,
            background: "var(--wf-paper)",
          }}
        >
          <IntegrationLogo integration={integration} size={48} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
              <h2
                className="wf-display"
                style={{
                  fontSize: 19,
                  fontWeight: 700,
                  letterSpacing: "-0.015em",
                  margin: 0,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {integration.name}
              </h2>
              {integration.recommended && (
                <span
                  className="wf-pill wf-pill--orange"
                  style={{ fontSize: 10, padding: "1px 7px", flexShrink: 0 }}
                >
                  <Star size={9} strokeWidth={2.5} /> recomendado
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--wf-ink-3)",
                fontWeight: 500,
                letterSpacing: "0.01em",
              }}
            >
              {integration.category}
              {!integration.runtimeReady && " · em breve"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{
              background: "transparent",
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 8,
              cursor: "pointer",
              padding: 6,
              color: "var(--wf-ink-2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </header>

        {/* Body — scroll */}
        <div style={{ flex: 1, overflow: "auto", padding: "20px 22px" }}>
          {/* Status block (when connected) */}
          {isConnected && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 10,
                background:
                  testResult?.kind === "ok"
                    ? "#eaf5ea"
                    : testResult?.kind === "fail"
                      ? "#fff1e6"
                      : "var(--wf-paper)",
                border: `1px solid ${
                  testResult?.kind === "ok"
                    ? "#5cb85c"
                    : testResult?.kind === "fail"
                      ? "var(--nd-orange)"
                      : "var(--wf-ink-5)"
                }`,
              }}
            >
              {testConnection.isPending && testResult === null ? (
                <>
                  <RefreshCw size={13} strokeWidth={1.75} className="wf-spin" color="var(--wf-ink-2)" />
                  <div style={{ fontSize: 13, color: "var(--wf-ink-2)" }}>
                    Validando conexão com {integration.name}…
                  </div>
                </>
              ) : testResult?.kind === "ok" ? (
                <>
                  <Check size={14} strokeWidth={2} color="#1f5c1f" />
                  <div style={{ flex: 1, fontSize: 13, color: "#1f5c1f" }}>
                    <strong>Conectado</strong> · {testResult.accountName}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setTestResult(null);
                      testConnection.mutate();
                    }}
                    title="Testar novamente"
                    className="wf-btn wf-btn--ghost wf-btn--sm"
                    style={{ padding: "4px 8px" }}
                  >
                    <RefreshCw size={11} strokeWidth={1.75} />
                  </button>
                </>
              ) : testResult?.kind === "fail" ? (
                <>
                  <AlertTriangle size={14} strokeWidth={1.75} color="var(--nd-orange-pun)" />
                  <div style={{ flex: 1, fontSize: 13, color: "var(--nd-orange-pun)" }}>
                    <strong>Conexão com problema:</strong> {testResult.error}
                  </div>
                </>
              ) : (
                <>
                  <Info size={14} strokeWidth={1.75} color="var(--wf-ink-3)" />
                  <div style={{ flex: 1, fontSize: 13, color: "var(--wf-ink-2)" }}>
                    Credenciais salvas. Clique pra validar.
                  </div>
                  <button
                    type="button"
                    onClick={() => testConnection.mutate()}
                    className="wf-btn wf-btn--ghost wf-btn--sm"
                  >
                    Testar
                  </button>
                </>
              )}
            </div>
          )}

          {/* Hero text */}
          <p style={{ fontSize: 13, color: "var(--wf-ink-2)", lineHeight: 1.55, marginBottom: 18 }}>
            {instructions.hero}
          </p>

          {/* Steps */}
          {instructions.steps.length > 0 && (
            <section style={{ marginBottom: 20 }}>
              <h3
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "var(--wf-ink-3)",
                  marginBottom: 10,
                }}
              >
                Como pegar suas credenciais
              </h3>
              <ol
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {instructions.steps.map((s, i) => (
                  <li key={i} style={{ display: "flex", gap: 10 }}>
                    <div
                      style={{
                        flexShrink: 0,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: "var(--nd-blue-deep)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: "var(--wf-font-display)",
                      }}
                    >
                      {i + 1}
                    </div>
                    <div style={{ flex: 1, paddingTop: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wf-ink)" }}>
                        {s.title}
                      </div>
                      {s.description && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--wf-ink-2)",
                            lineHeight: 1.5,
                            marginTop: 2,
                          }}
                        >
                          {s.description}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>

              <div style={{ marginTop: 12, display: "flex", gap: 14, fontSize: 12 }}>
                {instructions.docsUrl && (
                  <a
                    href={instructions.docsUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: "var(--nd-orange-pun)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      fontWeight: 500,
                      textDecoration: "none",
                    }}
                  >
                    <ExternalLink size={11} strokeWidth={1.75} /> Documentação oficial
                  </a>
                )}
                {instructions.futureTutorial && (
                  <span
                    style={{
                      color: "var(--wf-ink-3)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Lock size={11} strokeWidth={1.75} /> Aula em breve no The Members
                  </span>
                )}
              </div>
            </section>
          )}

          {/* Credential fields */}
          {integration.credentialFields.length > 0 && (
            <section style={{ marginBottom: 16 }}>
              <h3
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "var(--wf-ink-3)",
                  marginBottom: 10,
                }}
              >
                {isConnected ? "Atualizar credenciais (opcional)" : "Cole suas credenciais"}
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {integration.credentialFields.map((field) => (
                  <FieldInput
                    key={field.key}
                    field={field}
                    value={values[field.key] ?? ""}
                    onChange={(v) => setValues((prev) => ({ ...prev, [field.key]: v }))}
                  />
                ))}
              </div>
              {isConnected && (
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--wf-ink-3)",
                    marginTop: 10,
                    lineHeight: 1.5,
                  }}
                >
                  Os valores antigos não aparecem aqui por segurança. Deixe em branco pra manter o que já está salvo, ou preencha tudo pra substituir.
                </p>
              )}
            </section>
          )}

          {(connect.isError || disconnect.isError) && (
            <div
              style={{
                fontSize: 12,
                color: "var(--nd-orange-pun)",
                background: "#fff1e6",
                border: "1px solid var(--nd-orange)",
                borderRadius: 8,
                padding: "8px 12px",
                marginBottom: 12,
              }}
            >
              {connect.isError && `Erro ao conectar: ${connect.error.message}`}
              {disconnect.isError && `Erro ao desconectar: ${disconnect.error.message}`}
            </div>
          )}
        </div>

        {/* Footer — sticky bottom */}
        <footer
          style={{
            padding: "14px 22px",
            borderTop: "1px solid var(--wf-ink-5)",
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            background: "var(--wf-paper)",
            flexShrink: 0,
          }}
        >
          {isConnected ? (
            <button
              type="button"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
              className="wf-btn wf-btn--ghost wf-btn--sm"
              style={{ color: "var(--nd-orange-pun)" }}
            >
              {disconnect.isPending ? "desconectando…" : "Desconectar"}
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="wf-btn wf-btn--ghost wf-btn--sm"
              disabled={connect.isPending}
            >
              Cancelar
            </button>
          )}

          {isConnected ? (
            <button
              type="button"
              onClick={() => connect.mutate()}
              disabled={!hasAnyValue || requiredMissing || connect.isPending}
              className="wf-btn wf-btn--primary"
              style={{
                opacity: !hasAnyValue || requiredMissing || connect.isPending ? 0.5 : 1,
                cursor:
                  !hasAnyValue || requiredMissing || connect.isPending ? "not-allowed" : "pointer",
              }}
            >
              {connect.isPending ? "atualizando…" : "Atualizar credenciais"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => connect.mutate()}
              disabled={requiredMissing || connect.isPending}
              className="wf-btn wf-btn--primary"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                opacity: requiredMissing || connect.isPending ? 0.5 : 1,
                cursor: requiredMissing || connect.isPending ? "not-allowed" : "pointer",
              }}
            >
              {connect.isPending ? "conectando…" : "Conectar e testar"}
              {!connect.isPending && <ArrowRight size={12} strokeWidth={2} color="#fff" />}
            </button>
          )}
        </footer>
      </motion.aside>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Card
// ─────────────────────────────────────────────────────────────

/**
 * Integrações funcionais hoje. Tudo que NÃO está aqui aparece como
 * "Em breve" — card desabilitado com cadeado, sem permitir conexão.
 *
 * Conforme cada integração for implementada de verdade no backend
 * (services/integration-*.ts + cost tracking + snapshot pro prompt),
 * adiciona aqui pra desbloquear.
 */
const FUNCTIONAL_INTEGRATIONS = new Set<string>(["meta_ads", "google_ads"]);

function IntegrationCard({
  integration,
  onClick,
  delay = 0,
}: {
  integration: CompanyIntegration;
  onClick: () => void;
  delay?: number;
}) {
  const isConnected = integration.connection.connected;
  const isFunctional = FUNCTIONAL_INTEGRATIONS.has(integration.key);
  return (
    <motion.button
      type="button"
      onClick={isFunctional ? onClick : undefined}
      whileHover={isFunctional ? { y: -2 } : undefined}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay, ease: [0.16, 1, 0.3, 1] }}
      disabled={!isFunctional}
      style={{
        textAlign: "left",
        cursor: isFunctional ? "pointer" : "not-allowed",
        padding: 16,
        background: isFunctional ? "var(--wf-paper)" : "var(--wf-bg)",
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 10,
        boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        font: "inherit",
        color: "inherit",
        position: "relative",
        opacity: isFunctional ? 1 : 0.42,
        filter: isFunctional ? "none" : "grayscale(0.6)",
      }}
    >
      {/* Connected dot top-right (só pra funcionais) */}
      {isConnected && isFunctional && (
        <span
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#5cb85c",
            boxShadow: "0 0 0 3px rgba(92,184,92,0.18)",
          }}
        />
      )}

      {/* Pill "em breve" pra integrações ainda não implementadas */}
      {!isFunctional && (
        <span
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            background: "var(--wf-ink-6, #f3efe5)",
            color: "var(--wf-ink-2)",
            border: "1px solid var(--wf-ink-5)",
          }}
        >
          <Lock size={10} strokeWidth={2.5} />
          em breve
        </span>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <IntegrationLogo integration={integration} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "-0.005em",
              color: "var(--wf-ink)",
            }}
          >
            {integration.name}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--wf-ink-3)",
              fontWeight: 500,
              marginTop: 1,
            }}
          >
            {integration.category}
          </div>
        </div>
      </div>

      <div
        style={{
          fontSize: 12,
          color: "var(--wf-ink-2)",
          lineHeight: 1.5,
          flex: 1,
          minHeight: 36,
        }}
      >
        {integration.description}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {integration.recommended && (
          <span
            className="wf-pill wf-pill--orange"
            style={{ fontSize: 10, padding: "1px 7px" }}
          >
            <Star size={9} strokeWidth={2.5} /> recomendado
          </span>
        )}
        {integration.tags.slice(0, 2).map((tag) => (
          <span
            key={tag}
            className="wf-pill wf-pill--gray"
            style={{ fontSize: 10, padding: "1px 7px" }}
          >
            {tag}
          </span>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          paddingTop: 10,
          borderTop: "1px dashed var(--wf-ink-5)",
        }}
      >
        {isConnected ? (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#2a7a2a",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Check size={11} strokeWidth={2} /> conectado
          </span>
        ) : (
          <span style={{ fontSize: 11, color: "var(--wf-ink-3)", fontWeight: 500 }}>
            não conectado
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            fontSize: 11,
            color: "var(--nd-orange-pun)",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          {isConnected ? "gerenciar" : "conectar"}
          <ArrowRight size={11} strokeWidth={2} />
        </span>
      </div>
    </motion.button>
  );
}

// ─────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────

export default function Integracoes() {
  const companyId = useCompanyId();
  const [filter, setFilter] = useState<FilterTab>("todas");
  const [selected, setSelected] = useState<CompanyIntegration | null>(null);

  const integrations = useQuery({
    queryKey: ["companies", companyId, "integrations"],
    queryFn: () => integrationsApi.list(companyId!),
    enabled: !!companyId,
  });

  const filtered = useMemo(() => {
    const all = integrations.data ?? [];
    if (filter === "conectadas") return all.filter((i) => i.connection.connected);
    if (filter === "nao_conectadas") return all.filter((i) => !i.connection.connected);
    return all;
  }, [integrations.data, filter]);

  const grouped = useMemo(() => {
    return filtered.reduce<Record<string, CompanyIntegration[]>>((acc, i) => {
      if (!acc[i.category]) acc[i.category] = [];
      acc[i.category].push(i);
      return acc;
    }, {});
  }, [filtered]);

  const counts = useMemo(() => {
    const all = integrations.data ?? [];
    const connected = all.filter((i) => i.connection.connected).length;
    return {
      todas: all.length,
      conectadas: connected,
      nao_conectadas: all.length - connected,
    };
  }, [integrations.data]);

  if (!companyId) {
    return (
      <div style={{ padding: 32, color: "var(--wf-ink-2)" }} className="wf-hand-note">
        carregando empresa…
      </div>
    );
  }

  return (
    <div>
      {/* Header — sem hero pesado, apenas título + status compacto */}
      <div
        style={{
          padding: "28px 32px 16px",
          borderBottom: "1px solid var(--wf-ink-5)",
          background: "var(--wf-paper)",
        }}
      >
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 4,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(255,112,0,0.12)",
                color: "var(--nd-orange)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Plug size={16} strokeWidth={1.75} />
            </div>
            <h1
              className="wf-display"
              style={{
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Integrações
            </h1>
            <span
              style={{
                marginLeft: 8,
                fontSize: 12,
                fontWeight: 500,
                color: "var(--wf-ink-3)",
              }}
            >
              {counts.conectadas} de {counts.todas} conectadas
            </span>
          </div>
          <p
            style={{
              fontSize: 13,
              color: "var(--wf-ink-2)",
              marginTop: 6,
              maxWidth: 540,
            }}
          >
            Conecte suas ferramentas. Sua equipe trabalha melhor com dados reais do
            seu negócio.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="wf-tabs">
        {(
          [
            ["todas", `Todas (${counts.todas})`],
            ["conectadas", `Conectadas (${counts.conectadas})`],
            ["nao_conectadas", `Não conectadas (${counts.nao_conectadas})`],
          ] as Array<[FilterTab, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`wf-tab ${filter === key ? "wf-tab--active" : ""}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ padding: "24px 32px 48px", maxWidth: 1280, margin: "0 auto" }}>
        {integrations.isLoading && (
          <div className="wf-hand-note" style={{ fontSize: 13 }}>
            carregando catálogo…
          </div>
        )}

        {integrations.isError && (
          <div
            className="wf-card"
            style={{
              padding: 14,
              color: "var(--nd-orange-pun)",
              borderColor: "var(--nd-orange)",
            }}
          >
            erro ao carregar integrações: {(integrations.error as Error).message}
          </div>
        )}

        {integrations.isSuccess && (integrations.data?.length ?? 0) === 0 && (
          <div className="wf-card wf-card--dashed" style={{ padding: 24, textAlign: "center" }}>
            <div className="wf-hand-note" style={{ fontSize: 13 }}>
              catálogo vazio. rode <code>pnpm seed:nd:marketplace</code> no servidor
            </div>
          </div>
        )}

        {CATEGORY_ORDER.filter((cat) => grouped[cat]?.length).map((cat) => (
          <section key={cat} style={{ marginBottom: 28 }}>
            <header
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <h2
                className="wf-display"
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "var(--wf-ink-2)",
                  margin: 0,
                }}
              >
                {cat}
              </h2>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "var(--wf-ink-3)",
                }}
              >
                {grouped[cat].length}
              </span>
              <div style={{ flex: 1, borderTop: "1px dashed var(--wf-ink-5)" }} />
            </header>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 12,
              }}
            >
              {grouped[cat].map((integration, i) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  onClick={() => setSelected(integration)}
                  delay={i * 0.03}
                />
              ))}
            </div>
          </section>
        ))}

        {filtered.length === 0 &&
          integrations.isSuccess &&
          (integrations.data?.length ?? 0) > 0 && (
            <div className="wf-card wf-card--dashed" style={{ padding: 24, textAlign: "center" }}>
              <div className="wf-hand-note" style={{ fontSize: 13 }}>
                {filter === "conectadas"
                  ? "nada conectado ainda. comece pelas recomendadas (estrela laranja)"
                  : "nenhuma integração nesse filtro"}
              </div>
            </div>
          )}
      </div>

      <AnimatePresence>
        {selected && (
          <ConnectDrawer
            integration={selected}
            companyId={companyId}
            onClose={() => setSelected(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
